/* Relatório GHG Protocol: emissões por gás e escopo, CO₂ biogênico e tabelas por categoria. */

import { api } from '../../core/api';
import { baixarCSV } from '../../core/arquivos';
import { obraNome } from '../../core/catalogo';
import { cat, state } from '../../core/estado';
import { esc, lista, nf, num } from '../../core/formato';
import type { CategoriaGhg, ColunaGhg, LinhaGas, RelatorioGhg } from '../../tipos/api';
import { carregar } from '../../ui/carregar';
import { escLabel, kpi, loading, rotuloCategoria, tabela } from '../../ui/componentes';
import { ic } from '../../ui/dom';
import { filtrosBar, paramsFiltro } from '../../ui/filtros';
import { toast } from '../../ui/toast';
import type { Pagina } from '../pagina';

const COLUNAS: [ColunaGhg, string][] = [
  ['escopo_1', 'Escopo 1'], ['escopo_2_localizacao', 'Escopo 2 · localização'],
  ['escopo_2_escolha_compra', 'Escopo 2 · escolha de compra'], ['escopo_3', 'Escopo 3'],
];
const GAS: Record<string, string> = { CO2: 'CO₂', CH4: 'CH₄', N2O: 'N₂O', HFC: 'HFCs', PFC: 'PFCs', SF6: 'SF₆', NF3: 'NF₃' };

const somaColunas = (l: Partial<Record<ColunaGhg, number>> | undefined) => COLUNAS.reduce((a, [k]) => a + num(l?.[k]), 0);

function tabelaGases(linhas: LinhaGas[], total: LinhaGas | null, casas = 2): string {
  return tabela<LinhaGas>([
    { l: 'GEE', f: (x) => `<b>${GAS[x.gee] || esc(x.gee)}</b>` },
    ...COLUNAS.map(([k, l]) => ({ l, r: true, f: (x: LinhaGas) => nf(x[k], casas) })),
    { l: 'Total', r: true, f: (x) => `<b>${nf(somaColunas(x), casas)}</b>` },
  ], lista<LinhaGas>(linhas), total ? ['Total', ...COLUNAS.map(([k]) => nf(total[k], casas)), nf(somaColunas(total), casas)] : null);
}

function tabelaCategorias(linhas: CategoriaGhg[]): string {
  return tabela<CategoriaGhg>([
    { l: 'Categoria', f: (x) => `<span style="font-weight:600">${esc(rotuloCategoria(x.categoria_de_emissoes))}</span>` },
    { l: 'CO₂', r: true, f: (x) => nf(x.emissoes_co2, 2) },
    { l: 'CH₄', r: true, f: (x) => nf(x.emissoes_ch4, 2) },
    { l: 'N₂O', r: true, f: (x) => nf(x.emissoes_n2o, 2) },
    { l: 'HFCs', r: true, f: (x) => nf(x.emissoes_hfc, 2) },
    { l: 'Total tCO₂e', r: true, f: (x) => `<b>${nf(x.total_emissoes, 2)}</b>` },
    { l: 'CO₂ biogênico', r: true, f: (x) => nf(x.emissoes_co2_biogenico, 2) },
  ], lista<CategoriaGhg>(linhas).filter((x) => x.categoria_de_emissoes !== 'Sem dados'));
}

function relatorioHtml(d: RelatorioGhg): string {
  state.ghg = d;
  const outros = lista<{ gas: string; emissao_total: number }>(d.outros_gases_nao_quioto).filter((g) => g.gas !== 'Sem dados');
  const semEscopo = lista<string>(d.categorias_sem_escopo);
  const porEscopo = (e: 1 | 2 | 3) => d[`escopo_${e}_por_categoria`];
  return `
    <div class="grid g3" style="margin-bottom:22px">
      ${kpi('Emissões totais', nf(somaColunas(d.emissoes_tco2e_total)), 'tCO₂e', 'Soma dos escopos 1, 2 (localização e escolha de compra) e 3')}
      ${kpi('CO₂ biogênico', nf(somaColunas(d.emissoes_co2_biogenico_total)), 'tCO₂', 'Reportado à parte, fora do total')}
      ${kpi('Remoções de CO₂ biogênico', nf(somaColunas(d.remocoes_co2_biogenico_total)), 'tCO₂', 'Reportado à parte, fora do total')}
    </div>
    <h2 class="sec">Emissões por gás de efeito estufa (tCO₂e)</h2>
    ${tabelaGases(d.emissoes_tco2e, d.emissoes_tco2e_total)}
    <h2 class="sec" style="margin-top:28px">Emissões em toneladas métricas de cada gás (t)</h2>
    ${tabelaGases(d.emissoes_toneladas_metricas, null, 3)}
    <div class="grid g2" style="margin-top:28px;align-items:start">
      <div><h2 class="sec">CO₂ biogênico (t)</h2>${tabelaGases(lista<LinhaGas>(d.emissoes_co2_biogenico).filter((x) => x.gee === 'CO2'), null)}</div>
      <div><h2 class="sec">Remoções de CO₂ biogênico (t)</h2>${tabelaGases(lista<LinhaGas>(d.remocoes_co2_biogenico).filter((x) => x.gee === 'CO2'), null)}</div>
    </div>
    ${([1, 2, 3] as const).map((e) => `<h2 class="sec" style="margin-top:28px">${escLabel(e)} — por categoria (tCO₂e)</h2>${tabelaCategorias(porEscopo(e))}`).join('')}
    ${outros.length ? `<h2 class="sec" style="margin-top:28px">Outros gases (não-Quioto)</h2>${tabela([{ l: 'Gás', f: (x: { gas: string }) => esc(x.gas) }, { l: 'Emissão total', r: true, f: (x: { emissao_total: number }) => nf(x.emissao_total, 3) }], outros)}` : ''}
    ${semEscopo.length ? `<div class="callout neutral" style="margin-top:22px">${ic('i-info')}<div><b>Categorias sem escopo definido</b> (fora do total): ${semEscopo.map(esc).join(', ')}</div></div>` : ''}`;
}

function exportarGhg(): void {
  const d = state.ghg;
  if (!d) return toast('Aguarde o relatório carregar.');
  const linhas: unknown[][] = [['secao', 'gee', ...COLUNAS.map(([k]) => k)]];
  ([['tco2e', d.emissoes_tco2e], ['toneladas', d.emissoes_toneladas_metricas], ['co2_biogenico', d.emissoes_co2_biogenico], ['remocoes_biogenico', d.remocoes_co2_biogenico]] as [string, LinhaGas[]][])
    .forEach(([secao, l]) => lista<LinhaGas>(l).forEach((x) => linhas.push([secao, x.gee, ...COLUNAS.map(([k]) => num(x[k]))])));
  linhas.push([]);
  linhas.push(['escopo', 'categoria', 'co2', 'ch4', 'n2o', 'hfc', 'total_tco2e', 'co2_biogenico']);
  ([1, 2, 3] as const).forEach((e) => lista<CategoriaGhg>(d[`escopo_${e}_por_categoria`]).filter((x) => x.categoria_de_emissoes !== 'Sem dados').forEach((x) =>
    linhas.push([e, x.categoria_de_emissoes, num(x.emissoes_co2), num(x.emissoes_ch4), num(x.emissoes_n2o), num(x.emissoes_hfc), num(x.total_emissoes), num(x.emissoes_co2_biogenico)])));
  baixarCSV(`relatorio_ghg_${state.f.ano}.csv`, linhas);
}

export const relatorioGhg: Pagina = {
  titulo: 'Relatório GHG Protocol',

  render: () => `
  <div class="between wrap"><div><h1 class="page">Relatório GHG Protocol — ${esc(state.f.ano)}</h1>
    <p class="sub">${esc(cat().empresa.nome)}${state.f.obra ? ` · ${esc(obraNome(state.f.obra))}` : ''}. Valores consolidados a partir das emissões calculadas; nenhum GWP é recalculado na tela.</p></div>
    <div class="row no-print"><button class="btn btn-ghost" data-clique="csv">${ic('i-dl')} CSV</button>
      <button class="btn btn-ghost" data-clique="imprimir">${ic('i-print')} Imprimir</button></div></div>
  ${filtrosBar()}
  <div id="ghg">${loading('Montando relatório…')}</div>`,

  carregar(tk) {
    state.ghg = null;
    carregar(tk, [{
      id: 'ghg',
      busca: () => api<RelatorioGhg>('GET', '/relatorio-ghg', { params: paramsFiltro({ escopo: undefined }) }),
      html: relatorioHtml,
    }]);
  },

  acoes: {
    csv: exportarGhg,
    imprimir: () => window.print(),
  },
};
